import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  private $stateManagement = new BehaviorSubject<any>(null);
  private $state = this.$stateManagement.asObservable();

  // Error popup state
  private $errorPopup = new BehaviorSubject<{ show: boolean; message?: string; title?: string; closeable?: boolean }>({ show: false });
  public errorPopup$ = this.$errorPopup.asObservable();

  constructor() { }

  isLoggedIn(): boolean {
    // Implement your logic to check if the user is logged in
    return !!localStorage.getItem('token');
  }

  // Method to get state as observable
  getState(): Observable<any> {
    return this.$state;
  }

  // Method to set state
  setState(data: any): void {
    this.$stateManagement.next(data);
  }

  // Method to reset state
  resetState(): void {
    this.$stateManagement.next(null);
  }

  /**
   * Smart number formatting logic:
   * - If number is 1.5, show 1.50
   * - If number is 2, show 2 (not 2.00)
   * - If number is 1.3333333, show 1.33
   * - If useCommas is true, format with thousand separators (e.g., 1,234.56)
   */
  smartFormatNumber(value: number, decimalPlaces: number = 2, useCommas: boolean = false): string {
    if (value == null || isNaN(value)) {
      return '0';
    }

    // Round to specified decimal places
    const rounded = Math.round((value + Number.EPSILON) * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);

    let result: string;

    // Check if it's a whole number
    if (rounded % 1 === 0) {
      result = rounded.toString(); // Return whole number without decimals (e.g., "2")
    } else {
      // Check if it has only one or <decimalPlaces> decimal place
      const oneDecimal = Math.round((value + Number.EPSILON) * Math.pow(10, 1)) / Math.pow(10, 1);
      if (Math.abs(rounded - oneDecimal) < 0.001) {
        result = oneDecimal.toFixed(decimalPlaces); // Show with .X0 format (e.g., "1.50")
      } else {
        // For numbers with 2+ decimal places, show 2 decimal places
        result = rounded.toFixed(decimalPlaces); // Show with .XX format (e.g., "1.33")
      }
    }

    // Add thousand separators if requested
    if (useCommas) {
      const parts = result.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      result = parts.join('.');
    }

    return result;
  }

  /**
   * Show error popup with custom message
   * @param message - Error message to display
   * @param title - Error title
   * @param closeable - Whether the popup can be closed (default: true)
   */
  showErrorPopup(message: string = 'Something went wrong', title: string = 'Error', closeable: boolean = true): void {
    this.$errorPopup.next({ show: true, message, title, closeable });
  }

  /**
   * Show critical error popup (not closeable, requires action)
   * @param message - Error message to display
   * @param title - Error title
   */
  showCriticalError(message: string = 'Required details are missing. Please start a new flow.', title: string = 'Session Expired'): void {
    this.$errorPopup.next({ show: true, message, title, closeable: false });
  }

  /**
   * Hide error popup
   */
  hideErrorPopup(): void {
    this.$errorPopup.next({ show: false });
  }

  
}